// src/services/person/PersonService.ts
import { BaseTenantCrudService } from '@/services/_base/BaseTenantCrudService'
import { type Person, type PersonAttachment } from '@/types/Person.ts'
import type { IFirestoreService } from '@/platform/firebase/firestore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export class PersonService extends BaseTenantCrudService<Person> {
  constructor(firestore: IFirestoreService, session: Session) {
    super('people', firestore, session)
  }

  protected override beforeCreate(data: Partial<Person>): Partial<Person> {
    return {
      ...super.beforeCreate(data),
      firstName_lower: (data.firstName ?? '').toLowerCase(),
      lastName_lower: (data.lastName ?? '').toLowerCase(),
    }
  }

  protected override beforeUpdate(id: string, data: Partial<Person>): Partial<Person> {
    const normalized = { ...super.beforeUpdate(id, data) }

    if (data.firstName) {
      normalized.firstName_lower = data.firstName.toLowerCase()
    }
    if (data.lastName) {
      normalized.lastName_lower = data.lastName.toLowerCase()
    }

    return normalized
  }

  protected override async afterCreate(id: string, person: Partial<Person>) {
    // e.g., index to CRM timeline or trigger a welcome email
    console.log(`[PersonService] Created person ${id}`, person)
  }

  /** Soft delete a person by setting isDeleted flag */
  async softDelete(id: string): Promise<void> {
    await this.update(id, { isDeleted: true } as Partial<Person>)
  }

  /** Add an attachment to a person */
  async addAttachment(personId: string, file: globalThis.File): Promise<PersonAttachment> {
    const { getFileService } = await import('@/services/serviceFactory')
    const fileService = await getFileService()

    // Upload file to storage
    const path = `people/${personId}/attachments/${crypto.randomUUID()}`
    const { url } = await fileService.upload(file, path, {
      compress: file.type.startsWith('image/'),
    })

    // Create attachment object
    const attachment: PersonAttachment = {
      id: crypto.randomUUID(),
      name: file.name,
      fileType: file.type,
      url: url,
      uploadedAt: new Date(),
      category: 'other',
    }

    // Get current person
    const person = await this.firestore.getDoc<Person>(this.getCollectionPath(), personId)
    if (!person) {
      throw new Error('Person not found')
    }

    // Add attachment to person's attachments array
    const currentAttachments = person.attachments || []
    const updatedAttachments = [...currentAttachments, attachment]

    // Update person with new attachments
    await this.update(personId, {
      attachments: updatedAttachments,
    } as Partial<Person>)

    return attachment
  }

  /** Remove an attachment from a person */
  async removeAttachment(personId: string, attachmentId: string): Promise<void> {
    // Get current person
    const person = await this.firestore.getDoc<Person>(this.getCollectionPath(), personId)
    if (!person) {
      throw new Error('Person not found')
    }

    // Remove attachment from person's attachments array
    const currentAttachments = person.attachments || []
    const updatedAttachments = currentAttachments.filter((a) => a.id !== attachmentId)

    // Update person with updated attachments
    await this.update(personId, {
      attachments: updatedAttachments,
    } as Partial<Person>)
  }

  /** Update person with normalization of name fields */
  async updateWithNormalization(id: string, data: Partial<Person>): Promise<void> {
    await this.update(id, data)
  }
}
